/**
 * EventAlbumView — Feb 2026
 *
 * Full-screen album viewer. Header + masonry photo/video grid; clicking a
 * tile opens a lightbox with reactions (❤️), comments and face-tagging.
 * Tagged employees get an in-app notification (backend).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { X, Heart, MessageCircle, Tag, ChevronLeft, ChevronRight, Send, Trash2, Download, Loader2, Camera, Film } from "lucide-react";
import { mediaSrc } from "@/lib/authQuery";
import { Button } from "@/components/ui/button";

export default function EventAlbumView({ albumId, onClose, onChanged }) {
  const [album, setAlbum] = useState(null);
  const [busy, setBusy] = useState(true);
  const [lightbox, setLightbox] = useState(null); // media object or null
  const [downloading, setDownloading] = useState(false);
  const [params] = useSearchParams();

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/event-albums/${albumId}`);
      setAlbum(data);
      const targetMediaId = params.get("media");
      if (targetMediaId) {
        const found = (data.media || []).find((m) => m.id === targetMediaId);
        if (found) setLightbox(found);
      }
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load album")); onClose?.(); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [albumId]);

  const downloadZip = async () => {
    setDownloading(true);
    try {
      const resp = await api.get(`/event-albums/${albumId}/download-zip`, { responseType: "arraybuffer" });
      const blob = new Blob([resp.data], { type: "application/zip" });
      const a = document.createElement("a");
      const url = URL.createObjectURL(blob);
      a.href = url; a.download = `${album?.title || "event"}.zip`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErrorMessage(e, "Download failed")); }
    finally { setDownloading(false); }
  };

  if (!album && !busy) return null;

  // Feb 2026 R291n · Hero stats + first-media backdrop so the page
  // feels like a real event memento, not an empty detail screen.
  const mediaList = album?.media || [];
  const firstPhoto = mediaList.find((m) => m.kind === "photo");
  const totalReactions = mediaList.reduce((s, m) => s + (m.reaction_count || 0), 0);
  const totalComments = mediaList.reduce((s, m) => s + (m.comment_count || 0), 0);
  const totalTagged = new Set(mediaList.flatMap((m) => (m.tags || []).map((t) => t.employee_id))).size;
  const isSingle = mediaList.length === 1;

  return (
    <div className="fixed inset-0 z-[80] bg-gradient-to-br from-background via-background to-muted/30 overflow-y-auto" data-testid="event-album-view">
      {/* Sticky slim header — persists on scroll */}
      <div className="sticky top-0 z-20 bg-background/90 backdrop-blur-md border-b border-border">
        <div className="px-3 sm:px-6 py-2 flex items-center gap-3">
          <button onClick={onClose} className="p-1.5 hover:bg-muted rounded-md transition" data-testid="event-view-close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate">{album?.title || "Loading…"}</div>
          </div>
          {album && (
            <Button size="sm" variant="outline" onClick={downloadZip} disabled={downloading} data-testid="event-view-download" className="rounded-md">
              {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              {downloading ? "Zipping…" : "Download ZIP"}
            </Button>
          )}
        </div>
      </div>

      {busy ? (
        <div className="px-6 py-10 text-sm text-muted-foreground">Loading…</div>
      ) : (
        <>
          {/* HERO — big title over blurred first-photo backdrop */}
          <div className="relative overflow-hidden border-b border-border" data-testid="event-view-hero">
            {firstPhoto ? (
              <>
                <img
                  src={mediaSrc(firstPhoto.url)}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-40"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-background/70 via-background/55 to-background/85" aria-hidden="true" />
              </>
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-background" aria-hidden="true" />
            )}
            <div className="relative px-4 sm:px-10 py-6 sm:py-10">
              <div className="label-eyebrow text-[10px] text-primary mb-2 inline-flex items-center gap-1.5">
                <Camera className="w-3 h-3" /> Event Album
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight tracking-tight mb-3" style={{ fontFamily: "var(--font-heading)" }}>
                {album?.title}
              </h1>
              {album?.description && (
                <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mb-4">
                  {album.description}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[12px] text-muted-foreground">
                {album?.event_date && (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" aria-hidden="true" />
                    {album.event_date}
                  </span>
                )}
                {album?.location && (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
                    {album.location}
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5">
                  <Camera className="w-3 h-3" /> {mediaList.length} {mediaList.length === 1 ? "photo" : "photos"}
                </span>
                {totalReactions > 0 && (
                  <span className="inline-flex items-center gap-1.5 text-rose-600">
                    <Heart className="w-3 h-3 fill-current" /> {totalReactions}
                  </span>
                )}
                {totalComments > 0 && (
                  <span className="inline-flex items-center gap-1.5">
                    <MessageCircle className="w-3 h-3" /> {totalComments}
                  </span>
                )}
                {totalTagged > 0 && (
                  <span className="inline-flex items-center gap-1.5">
                    <Tag className="w-3 h-3" /> {totalTagged} tagged
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Gallery */}
          <div className="px-3 sm:px-6 py-5 sm:py-8 max-w-6xl mx-auto">
            {mediaList.length === 0 ? (
              <div className="text-center py-20 text-sm text-muted-foreground">
                <Camera className="w-10 h-10 mx-auto mb-3 opacity-40" />
                No media uploaded yet.
              </div>
            ) : isSingle ? (
              // Single-media "spotlight" layout — one photo deserves the stage.
              <div className="flex justify-center" data-testid="event-view-grid">
                <button
                  type="button"
                  onClick={() => setLightbox(mediaList[0])}
                  className="group relative max-w-3xl w-full overflow-hidden rounded-xl border border-border/70 bg-muted shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
                  data-testid={`event-view-tile-${mediaList[0].id}`}
                >
                  {mediaList[0].kind === "photo" ? (
                    <img src={mediaSrc(mediaList[0].url)} alt={mediaList[0].caption || ""} className="w-full h-auto object-cover" loading="lazy" />
                  ) : (
                    <div className="aspect-video w-full flex items-center justify-center bg-black text-white">
                      <Film className="w-16 h-16 opacity-70" />
                    </div>
                  )}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent p-4 flex gap-5 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="inline-flex items-center gap-1.5"><Heart className="w-3.5 h-3.5" />{mediaList[0].reaction_count || 0}</span>
                    <span className="inline-flex items-center gap-1.5"><MessageCircle className="w-3.5 h-3.5" />{mediaList[0].comment_count || 0}</span>
                    {(mediaList[0].tags || []).length > 0 && (
                      <span className="inline-flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" />{mediaList[0].tags.length}</span>
                    )}
                    <span className="ml-auto text-[10px] opacity-80 uppercase tracking-widest">Click to open</span>
                  </div>
                </button>
              </div>
            ) : (
              // Masonry grid — rounded tiles, hover elevation + overlay stats.
              <div className="columns-2 sm:columns-3 lg:columns-4 gap-3 space-y-3" data-testid="event-view-grid">
                {mediaList.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setLightbox(m)}
                    className="relative w-full break-inside-avoid group overflow-hidden rounded-lg border border-border/70 bg-muted shadow-sm hover:shadow-xl transition-all duration-200 hover:-translate-y-0.5"
                    data-testid={`event-view-tile-${m.id}`}
                  >
                    {m.kind === "photo" ? (
                      <img src={mediaSrc(m.thumb_url || m.url)} alt={m.caption || ""} className="w-full h-auto object-cover group-hover:scale-[1.03] transition-transform duration-300" loading="lazy" />
                    ) : (
                      <div className="aspect-video w-full flex items-center justify-center bg-black text-white relative">
                        <Film className="w-10 h-10 opacity-70" />
                        <span className="absolute bottom-1 right-1 text-[9px] bg-white/20 px-1.5 py-0.5 uppercase tracking-widest rounded">Video</span>
                      </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent p-2 flex gap-3 text-[10px] text-white opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="inline-flex items-center gap-1"><Heart className="w-3 h-3" />{m.reaction_count || 0}</span>
                      <span className="inline-flex items-center gap-1"><MessageCircle className="w-3 h-3" />{m.comment_count || 0}</span>
                      {(m.tags || []).length > 0 && (
                        <span className="inline-flex items-center gap-1"><Tag className="w-3 h-3" />{m.tags.length}</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {lightbox && (
        <Lightbox
          media={lightbox}
          album={album}
          onClose={() => setLightbox(null)}
          onNav={(dir) => {
            const list = album?.media || [];
            const idx = list.findIndex((x) => x.id === lightbox.id);
            const next = list[idx + dir];
            if (next) setLightbox(next);
          }}
          onChanged={() => { load(); onChanged?.(); }}
        />
      )}
    </div>
  );
}


function Lightbox({ media, album, onClose, onNav, onChanged }) {
  const [reacted, setReacted] = useState(!!media.viewer_reacted);
  const [count, setCount] = useState(media.reaction_count || 0);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [posting, setPosting] = useState(false);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [tagQuery, setTagQuery] = useState("");
  const imgRef = useRef(null);
  const [pending, setPending] = useState(null); // {x, y} click coords for a new tag

  useEffect(() => {
    setReacted(!!media.viewer_reacted);
    setCount(media.reaction_count || 0);
    setPending(null);
    setShowTagPicker(false);
    api.get(`/event-media/${media.id}/comments`)
      .then((r) => setComments(r.data?.rows || []))
      .catch(() => setComments([]));
  }, [media.id]);

  useEffect(() => {
    // r-mentions · load employees eagerly so @mention autocomplete works
    // the moment the lightbox opens. The tag-picker also reuses this list.
    if (employees.length === 0 && album?.workspace_id) {
      api.get(`/workspaces/${album.workspace_id}/employees`)
        .then((r) => setEmployees(Array.isArray(r.data) ? r.data : (r.data?.employees || [])))
        .catch(() => setEmployees([]));
    }
  }, [album?.workspace_id, employees.length]);

  const toggleReact = async () => {
    try {
      const { data } = await api.post(`/event-media/${media.id}/react`);
      setReacted(!!data.reacted);
      setCount(data.count || 0);
    } catch (e) { toast.error(apiErrorMessage(e, "Reaction failed")); }
  };

  const submitComment = async () => {
    if (!commentText.trim()) return;
    setPosting(true);
    try {
      await api.post(`/event-media/${media.id}/comments`, { body: commentText.trim() });
      setCommentText("");
      const { data } = await api.get(`/event-media/${media.id}/comments`);
      setComments(data?.rows || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Comment failed")); }
    finally { setPosting(false); }
  };

  const deleteComment = async (c) => {
    try {
      await api.delete(`/event-media/${media.id}/comments/${c.id}`);
      setComments((rows) => rows.filter((r) => r.id !== c.id));
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const onImgClick = (e) => {
    if (!album?.can_edit && !album?.is_moderator) return;
    if (!showTagPicker) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const xPct = ((e.clientX - rect.left) / rect.width) * 100;
    const yPct = ((e.clientY - rect.top) / rect.height) * 100;
    setPending({ x: xPct, y: yPct });
  };

  const addTagFor = async (emp) => {
    if (!pending) return;
    const next = [...(media.tags || []).map((t) => ({ employee_id: t.employee_id, x_pct: t.x_pct, y_pct: t.y_pct })),
      { employee_id: emp.id, x_pct: pending.x, y_pct: pending.y }];
    try {
      const { data } = await api.patch(`/event-media/${media.id}`, { tags: next });
      toast.success(`Tagged ${emp.full_name}${data.tagged_user_count ? " · notified" : ""}`);
      setPending(null); setTagQuery(""); setShowTagPicker(false);
      onChanged?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Tag failed")); }
  };

  const removeTag = async (t) => {
    const next = (media.tags || []).filter((x) => x.employee_id !== t.employee_id)
      .map((t) => ({ employee_id: t.employee_id, x_pct: t.x_pct, y_pct: t.y_pct }));
    try {
      await api.patch(`/event-media/${media.id}`, { tags: next });
      onChanged?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Untag failed")); }
  };

  const filtered = useMemo(() => {
    const q = tagQuery.trim().toLowerCase();
    if (!q) return employees.slice(0, 20);
    return employees.filter((e) => (e.full_name || "").toLowerCase().includes(q)
      || (e.email_official || e.email || "").toLowerCase().includes(q)).slice(0, 20);
  }, [tagQuery, employees]);

  return (
    <div className="fixed inset-0 z-[90] bg-black/95 flex flex-col lg:flex-row" data-testid="event-lightbox">
      {/* Left — media */}
      <div className="relative flex-1 flex items-center justify-center overflow-hidden">
        <button onClick={onClose} className="absolute top-3 left-3 z-20 p-2 bg-white/10 hover:bg-white/20 text-white" data-testid="event-lightbox-close"><X className="w-5 h-5" /></button>
        <button onClick={() => onNav(-1)} className="absolute left-2 top-1/2 -translate-y-1/2 z-20 p-2 bg-white/10 hover:bg-white/20 text-white" data-testid="event-lightbox-prev"><ChevronLeft className="w-6 h-6" /></button>
        <button onClick={() => onNav(1)} className="absolute right-2 top-1/2 -translate-y-1/2 z-20 p-2 bg-white/10 hover:bg-white/20 text-white" data-testid="event-lightbox-next"><ChevronRight className="w-6 h-6" /></button>
        {media.kind === "photo" ? (
          <div className="relative max-h-full max-w-full">
            <img
              ref={imgRef}
              src={mediaSrc(media.url)}
              alt={media.caption || ""}
              className={`max-h-[100vh] max-w-full object-contain ${showTagPicker ? "cursor-crosshair" : ""}`}
              onClick={onImgClick}
            />
            {/* Existing tags */}
            {(media.tags || []).map((t) => (
              <div
                key={t.employee_id}
                className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-auto"
                style={{ left: `${t.x_pct}%`, top: `${t.y_pct}%` }}
              >
                <div className="relative group">
                  <div className="w-6 h-6 rounded-full border-2 border-white shadow-[0_0_0_2px_rgba(0,0,0,0.4)] bg-fuchsia-500/40 group-hover:scale-110 transition-transform" />
                  <div className="absolute left-1/2 -translate-x-1/2 top-8 whitespace-nowrap bg-black/80 text-white text-[11px] px-2 py-0.5 opacity-0 group-hover:opacity-100 flex items-center gap-1">
                    {t.full_name}
                    {(album?.can_edit || album?.is_moderator) && (
                      <button onClick={(e) => { e.stopPropagation(); removeTag(t); }} className="ml-1 text-red-300 hover:text-red-500" data-testid={`event-untag-${t.employee_id}`}>
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {/* Pending tag marker */}
            {pending && (
              <div className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                   style={{ left: `${pending.x}%`, top: `${pending.y}%` }}>
                <div className="w-8 h-8 rounded-full border-2 border-white animate-pulse bg-fuchsia-500/60" />
              </div>
            )}
          </div>
        ) : (
          <video src={mediaSrc(media.url)} controls className="max-h-[100vh] max-w-full" />
        )}
      </div>

      {/* Right — engagement panel */}
      <aside className="w-full lg:w-96 bg-background text-foreground flex flex-col max-h-[50vh] lg:max-h-full">
        <div className="px-4 py-3 border-b border-border">
          {media.caption && <div className="text-sm">{media.caption}</div>}
          <div className="flex items-center gap-2 mt-2">
            <button onClick={toggleReact} className={`flex items-center gap-1 px-2 py-1 border ${reacted ? "bg-fuchsia-500 text-white border-fuchsia-500" : "border-border"}`} data-testid="event-react-btn">
              <Heart className={`w-4 h-4 ${reacted ? "fill-current" : ""}`} />
              <span className="text-xs font-semibold">{count}</span>
            </button>
            {media.kind === "photo" && (album?.can_edit || album?.is_moderator) && (
              <button onClick={() => setShowTagPicker((s) => !s)} className={`flex items-center gap-1 px-2 py-1 border ${showTagPicker ? "bg-primary text-primary-foreground" : "border-border"}`} data-testid="event-tag-btn">
                <Tag className="w-4 h-4" /><span className="text-xs">{showTagPicker ? "Click the photo" : "Tag someone"}</span>
              </button>
            )}
            <div className="ml-auto text-[11px] text-muted-foreground">by {media.uploaded_by_name}</div>
          </div>
        </div>

        {/* Tag picker */}
        {showTagPicker && pending && (
          <div className="px-4 py-2 border-b border-border bg-muted/30" data-testid="event-tag-picker">
            <div className="text-[11px] text-muted-foreground mb-1">Pick a colleague to tag at this spot</div>
            <input
              autoFocus
              value={tagQuery}
              onChange={(e) => setTagQuery(e.target.value)}
              placeholder="Search employees…"
              className="w-full text-sm px-2 py-1 border border-border bg-background"
              data-testid="event-tag-search"
            />
            <div className="mt-1 max-h-40 overflow-y-auto">
              {filtered.map((e) => (
                <button
                  key={e.id}
                  onClick={() => addTagFor(e)}
                  className="w-full text-left text-xs px-2 py-1 hover:bg-primary/10"
                  data-testid={`event-tag-emp-${e.id}`}
                >{e.full_name} <span className="text-muted-foreground">· {e.email_official || e.email}</span></button>
              ))}
              {filtered.length === 0 && <div className="text-xs text-muted-foreground py-2">No matches</div>}
            </div>
          </div>
        )}

        {/* Comments */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="event-comments-list">
          {comments.length === 0 ? (
            <div className="text-xs text-muted-foreground">Be the first to comment 🎉</div>
          ) : comments.map((c) => (
            <div key={c.id} className="text-xs" data-testid={`event-comment-${c.id}`}>
              <div className="flex items-center gap-2">
                <div className="font-semibold">{c.user_name}</div>
                <div className="text-[10px] text-muted-foreground">{c.created_at?.slice(11, 16)}</div>
                <button onClick={() => deleteComment(c)} className="ml-auto text-muted-foreground hover:text-destructive" title="Delete">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
              <div className="mt-0.5 whitespace-pre-wrap">{renderMentions(c.body)}</div>
            </div>
          ))}
        </div>

        <MentionCommentInput
          employees={employees}
          value={commentText}
          onChange={setCommentText}
          onSubmit={submitComment}
          posting={posting}
        />
      </aside>
    </div>
  );
}

/** Highlight @mentions inline. Safe splitting — never renders raw HTML. */
function renderMentions(text) {
  if (!text) return null;
  const parts = String(text).split(/(@[A-Za-z][A-Za-z0-9._\- ]{1,60}?(?=[\s.,;:!?]|$))/g);
  return parts.map((chunk, i) => {
    if (chunk.startsWith("@") && chunk.length > 1) {
      return (
        <span key={i} className="text-primary font-semibold bg-primary/10 rounded px-0.5">
          {chunk}
        </span>
      );
    }
    return <span key={i}>{chunk}</span>;
  });
}

/** Comment box with @mention popover autocomplete. Falls back to plain
 *  text if no matches. Keyboard-navigable with ArrowUp / ArrowDown /
 *  Enter to insert; Esc closes the popover. */
function MentionCommentInput({ employees, value, onChange, onSubmit, posting }) {
  const [showMention, setShowMention] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionCursor, setMentionCursor] = useState(0);
  const inputRef = useRef(null);

  // Detect an active @… token at the caret so the popover only shows
  // while the user is actively typing a mention.
  const updateMentionState = (val, caretPos) => {
    const upTo = val.slice(0, caretPos);
    const m = upTo.match(/(?:^|\s)@([A-Za-z][A-Za-z0-9._\- ]{0,60})$/);
    if (m) {
      setShowMention(true);
      setMentionQuery(m[1]);
      setMentionCursor(0);
    } else {
      setShowMention(false);
    }
  };

  const matches = useMemo(() => {
    const q = mentionQuery.trim().toLowerCase();
    if (!q) return employees.slice(0, 6);
    return employees
      .filter((e) =>
        (e.full_name || "").toLowerCase().includes(q)
        || (e.email_official || e.email || "").toLowerCase().includes(q)
      )
      .slice(0, 6);
  }, [mentionQuery, employees]);

  const insertMention = (emp) => {
    const el = inputRef.current;
    if (!el) return;
    const pos = el.selectionStart || value.length;
    const before = value.slice(0, pos);
    const after = value.slice(pos);
    const replaced = before.replace(/@[A-Za-z][A-Za-z0-9._\- ]{0,60}$/,
      `@${emp.full_name} `);
    onChange(replaced + after);
    setShowMention(false);
    setTimeout(() => {
      el.focus();
      const nextPos = replaced.length;
      el.setSelectionRange(nextPos, nextPos);
    }, 0);
  };

  const onKey = (e) => {
    if (showMention && matches.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setMentionCursor((c) => Math.min(c + 1, matches.length - 1)); return; }
      if (e.key === "ArrowUp")   { e.preventDefault(); setMentionCursor((c) => Math.max(c - 1, 0)); return; }
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault(); insertMention(matches[mentionCursor]); return;
      }
      if (e.key === "Escape") { setShowMention(false); return; }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <div className="relative px-3 py-2 border-t border-border" data-testid="event-comment-editor">
      {showMention && matches.length > 0 && (
        <div className="absolute left-3 right-3 bottom-full mb-1 bg-background border border-border shadow-lg z-10 max-h-56 overflow-y-auto" data-testid="event-mention-popover">
          {matches.map((emp, i) => (
            <button
              key={emp.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); insertMention(emp); }}
              onMouseEnter={() => setMentionCursor(i)}
              className={`w-full px-3 py-1.5 flex items-center gap-2 text-xs text-left ${i === mentionCursor ? "bg-muted/60" : "hover:bg-muted/40"}`}
              data-testid={`event-mention-opt-${emp.id}`}
            >
              <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-[9px] font-bold shrink-0">
                {(emp.full_name || "?").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="font-medium truncate">{emp.full_name}</div>
                {(emp.email_official || emp.email) && (
                  <div className="text-[10px] text-muted-foreground truncate">{emp.email_official || emp.email}</div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            updateMentionState(e.target.value, e.target.selectionStart || e.target.value.length);
          }}
          onKeyDown={onKey}
          onKeyUp={(e) => updateMentionState(e.target.value, e.target.selectionStart || e.target.value.length)}
          placeholder="Add a comment… use @ to mention"
          className="flex-1 text-sm px-2 py-1.5 border border-border bg-background"
          data-testid="event-comment-input"
        />
        <button onClick={onSubmit} disabled={posting || !value.trim()} className="p-1.5 bg-primary text-primary-foreground disabled:opacity-50" data-testid="event-comment-submit">
          {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
